<template>
  <div>
    <!-- Single persistent polite live region. Always rendered and visually hidden;
         its text mirrors the active transient state so screen readers announce
         build/install changes. The matching blocks below stay the visible
         UI but no longer carry aria-live (they mount with their text, so an
         in-block live region would not reliably announce). -->
    <div class="kvm-sr-status" aria-live="polite">{{ statusAnnouncement }}</div>

    <!-- Billing lifecycle banner — suspended/terminated services aren't normal. -->
    <v-alert v-if="server.entitlement_status === 'suspended'" type="warning" border="left" prominent class="mb-4">
      This service is <strong>suspended</strong>. The server is stopped until it is reactivated.
    </v-alert>
    <v-alert v-else-if="server.entitlement_status === 'terminated'" type="error" border="left" prominent class="mb-4">
      This service has been <strong>terminated</strong>.
    </v-alert>

    <!-- Initial load (no service data yet) -->
    <preloader v-if="loading && !server.provisioning_state" />

    <!-- Load failed — show why, with a way to recover (instead of a blank spinner). -->
    <gg-card v-else-if="error">
      <gg-card-text class="pa-4">
        <v-alert type="error" border="left" prominent class="mb-4">
          {{ error.message || 'This service could not be loaded.' }}
        </v-alert>
        <gg-btn tile color="primary" class="mr-2" :loading="loading" @click="loadServer">
          <v-icon left small>mdi-refresh</v-icon>Retry
        </gg-btn>
        <gg-btn tile outlined color="primary" :to="{ name: 'hosting_kvm_list' }">
          Back to servers
        </gg-btn>
      </gg-card-text>
    </gg-card>

    <!-- Unprovisioned: the customer picks region + OS/App before a VM exists. -->
    <hosting-kvm-provision
      v-else-if="server.provisioning_state === 'unprovisioned'"
      :entitlement-id="server.id"
      :region="server.location !== '—' ? server.location : ''"
      @provisioning="onProvisioningStarted"
    />

    <!-- Building: entitlement provisioning, or VM still coming up — poll until the
         VM actually settles (running/stopped) before showing the dashboard. -->
    <gg-card v-else-if="isBuilding">
      <gg-card-text class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" size="48" width="4" class="mb-4" aria-hidden="true" />
        <div class="body-1 mb-1">{{ buildMessage }}</div>
        <div class="kvm-shell__hint">This usually takes a minute or two. The page updates automatically.</div>
      </gg-card-text>
    </gg-card>

    <!-- Deploying: the VM is up but the app is still installing in-guest. Show live
         progress (a step checklist when detailed, else a percent bar / spinner) with
         the live cloud-init log streaming below. -->
    <div v-else-if="isInstalling">
      <gg-card>
        <gg-card-text class="pa-6">
          <div class="d-flex align-center mb-4">
            <v-progress-circular indeterminate color="primary" size="28" width="3" class="mr-3" aria-hidden="true" />
            <div class="body-1">{{ install.label || 'Setting up your server…' }}</div>
          </div>

          <template v-if="install.detailed">
            <v-progress-linear :value="install.percent || 0" height="8" rounded color="primary" class="mb-1" aria-hidden="true" />
            <div class="kvm-shell__hint text-caption mb-4">{{ install.percent || 0 }}%</div>
          </template>

          <v-list v-if="installSteps.length" dense class="py-0">
            <v-list-item v-for="step in installSteps" :key="step.key" class="px-0" style="min-height: 32px;">
              <v-icon small class="mr-2" :color="stepColor(step.status)" aria-hidden="true">{{ stepIcon(step.status) }}</v-icon>
              <span :class="{ 'kvm-shell__muted': step.status === 'pending' || step.status === 'skipped' }">{{ step.label }}</span>
              <span class="kvm-shell__sr"> — {{ step.status }}</span>
            </v-list-item>
          </v-list>

          <div class="kvm-shell__hint mt-4">This page updates automatically — you don't need to refresh.</div>

          <kvm-deploy-log
            :entitlement-id="server.id" :capable="deployLogsCapable" :active="isInstalling" class="mt-5"
          />
        </gg-card-text>
      </gg-card>
    </div>

    <!-- Deployment failed — the in-guest app install errored. Shown on the Overview
         only; sub-pages (Rebuild, Console, …) stay reachable via the dashboard shell. -->
    <div v-else-if="installFailed && onIndex">
      <gg-card>
        <gg-card-text class="pa-4">
          <v-alert type="error" border="left" prominent class="mb-4">
            {{ (server.install && server.install.label) || 'Your server was created but the application failed to install.' }}
          </v-alert>
          <gg-btn tile color="primary" class="mr-2" :loading="loading" @click="loadServer">
            <v-icon left small>mdi-refresh</v-icon>Refresh
          </gg-btn>
          <gg-btn tile outlined color="primary" class="mr-2"
                  :to="{ name: 'hosting_kvm_rebuild', params: { hostingid: server.id } }">
            Rebuild
          </gg-btn>
          <gg-btn tile outlined color="primary" :to="{ name: 'hosting_kvm_list' }">
            Back to servers
          </gg-btn>

          <kvm-deploy-log
            :entitlement-id="server.id" :capable="deployLogsCapable" :active="false" class="mt-5"
          />
        </gg-card-text>
      </gg-card>
    </div>

    <!-- Build failed — the VM landed in an error state (Overview only). -->
    <gg-card v-else-if="buildFailed && onIndex">
      <gg-card-text class="pa-4">
        <v-alert type="error" border="left" prominent class="mb-4">
          {{ server.last_error || 'Your server could not be provisioned. Please contact support if this persists.' }}
        </v-alert>
        <gg-btn tile color="primary" class="mr-2" :loading="loading" @click="loadServer">
          <v-icon left small>mdi-refresh</v-icon>Refresh
        </gg-btn>
        <gg-btn tile outlined color="primary" class="mr-2"
                :to="{ name: 'hosting_kvm_rebuild', params: { hostingid: server.id } }">
          Rebuild
        </gg-btn>
        <gg-btn tile outlined color="primary" :to="{ name: 'hosting_kvm_list' }">
          Back to servers
        </gg-btn>
      </gg-card-text>
    </gg-card>

    <!-- Ready: provisioned and the VM has started — the normal VM dashboard. -->
    <div v-else-if="isReady">
      <!-- Persistent server information — stays across every sub-page -->
      <v-row class="mb-6">
        <v-col cols="12">
          <gg-card>
            <gg-card-text class="pa-3">
              <div class="d-flex flex-wrap align-center mb-3">
                <v-icon size="24" color="primary" class="pb-1 mr-2" aria-hidden="true">mdi-server</v-icon>
                <span class="body-1">{{ server.label }}</span>
                <gg-btn icon small class="mr-2" aria-label="Rename this server" title="Rename" @click="openRename">
                  <v-icon small>mdi-pencil</v-icon>
                </gg-btn>
                <!-- Status dot + text, as the portal shows a service's status elsewhere. -->
                <span class="mr-auto" role="status" aria-live="polite">
                  <v-icon small class="pb-1" :color="powerColor" aria-hidden="true">mdi-circle</v-icon>
                  <span class="kvm-shell__sr">Power state: </span>
                  <span class="pl-1 text-capitalize">{{ powerLabel }}</span>
                </span>
                <kvm-power-menu :server="server" :pending="pendingPower" @action="runPower" />
              </div>
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="kvm-shell__label">Hostname</div>
                  <div>{{ server.hostname }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="kvm-shell__label">Primary IP</div>
                  <div><kvm-copyable :value="server.primary_ip" /></div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="kvm-shell__label">Location</div>
                  <div>{{ server.location }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="kvm-shell__label">Operating System</div>
                  <div>{{ server.os }}</div>
                </v-col>
              </v-row>
            </gg-card-text>
          </gg-card>
        </v-col>
      </v-row>

      <!-- Active section content. The VNC console is kept alive so its live session
           survives navigation between this VM's pages (its canvas re-attaches
           cleanly); it is torn down only when KvmServiceView is destroyed (we leave
           the VM). The Terminal is NOT kept alive — xterm's DOM doesn't repaint
           reliably after a keep-alive detach/re-attach, so it mounts fresh (and
           reconnects, which is instant for serial) each time it is opened. -->
      <gg-loading :loading="loading" type="image" height="400">
        <keep-alive include="HostingKvmConsole">
          <router-view :server="server" :loading="loading" :on-refresh="loadServer" :on-power-change="watchPower" />
        </keep-alive>
      </gg-loading>
    </div>

    <preloader v-else />

    <!-- Rename: the server's display label (panel only — the hostname is separate). -->
    <v-dialog v-model="rename.open" max-width="440">
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-rename-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-rename-title" class="body-1 font-weight-medium mb-2">Rename this server</h3>
          <p class="text-caption mb-3">
            A name for this server in your account. It doesn't change anything on the server — the hostname is
            set separately (VPS Details → Hostname). Leave it empty to show the hostname.
          </p>
          <v-text-field v-model="rename.value" label="Server name" outlined dense counter="64" maxlength="64"
                        :placeholder="server.hostname" autocomplete="off" @keyup.enter="saveRename" />
          <v-alert v-if="rename.error" type="warning" dense text class="mb-0" role="alert">{{ rename.error }}</v-alert>
          <div class="text-right mt-3">
            <gg-btn text class="mr-2" :disabled="rename.saving" @click="rename.open = false">Cancel</gg-btn>
            <gg-btn tile color="primary" :loading="rename.saving" @click="saveRename">Save</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'
import Preloader from '@/components/Common/Preloader'
import HostingKvmProvision from '@/views/services/hosting/kvm/HostingKvmProvision'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'
import KvmDeployLog from '@/views/services/hosting/kvm/KvmDeployLog'
import KvmPowerMenu from '@/views/services/hosting/kvm/KvmPowerMenu'
import { setKvmFeature } from '@/helpers/kvmFeatures'

const POLL_INTERVAL = 5000
// Power-state follow-up after a start/stop/reboot: every 3s for up to ~90s (a
// guest that ignores ACPI can take a while to power off).
const POWER_WATCH_INTERVAL = 3000
const POWER_WATCH_TRIES = 30

// Header badge text while a power action is settling.
const PENDING_LABELS = { start: 'Starting…', stop: 'Powering off…', reboot: 'Rebooting…' }

export default {
  name: 'KvmServiceView',
  components: { Preloader, HostingKvmProvision, KvmCopyable, KvmDeployLog, KvmPowerMenu },
  props: {
    hostingid: {
      required: true,
      type: [String, Number],
    },
  },
  data: () => ({
    loading: false,
    error: null,
    server: {},
    pollTimer: null,
    powerTimer: null,
    // A power action in flight or not yet settled: { action, fromUptime }. Locks
    // the header power menu; cleared once the server reaches the new state.
    pendingPower: null,
    pendingTimer: null,
    rename: { open: false, value: '', saving: false, error: '' },
  }),
  computed: {
    // The build is still in progress while the entitlement is provisioning OR it
    // has provisioned but the VM itself hasn't finished coming up yet.
    isBuilding () {
      const s = this.server
      return s.provisioning_state === 'provisioning' ||
        (s.provisioning_state === 'provisioned' && s.vm_status === 'provisioning')
    },
    // The VM has booted but the in-guest app install is still running. This is a
    // distinct phase from the build: provisioning_state is 'provisioned' and the VM
    // runs while `install.state` is 'installing'.
    install () {
      return this.server.install || null
    },
    isInstalling () {
      return !!this.install && this.install.state === 'installing'
    },
    installFailed () {
      return !!this.install && this.install.state === 'failed'
    },
    installSteps () {
      return (this.install && this.install.steps) || []
    },
    // Whether the deployed app exposes the live cloud-init log (gates the panel).
    deployLogsCapable () {
      return !!(this.server.capabilities && this.server.capabilities.deploy_logs)
    },
    // Provisioned but the VM landed in an error state — the build failed.
    buildFailed () {
      return this.server.provisioning_state === 'provisioned' && this.server.vm_status === 'error'
    },
    // Provisioned and the VM has settled (not building/installing) — the
    // dashboard shell (server info + sub-page router-view) renders. Failed states
    // (build error / install failed) still show the shell so management sub-pages —
    // crucially Rebuild — remain reachable; the failure takes over only the Overview
    // (index) route via the `*Failed && onIndex` gates below.
    isReady () {
      const s = this.server
      return s.provisioning_state === 'provisioned' && !this.isBuilding && !this.isInstalling
    },
    // The Overview (index) route — where a failed state shows its full screen.
    onIndex () {
      return !!this.$route && this.$route.name === 'hosting_kvm_info'
    },
    buildMessage () {
      return this.server.provisioning_state === 'provisioned'
        ? 'Your server is starting up…'
        : 'Your server is being provisioned…'
    },
    // Human status string for the single persistent live region. Mirrors the
    // active transient state (build / install); empty once the
    // dashboard has settled so nothing is announced.
    statusAnnouncement () {
      if (this.isBuilding) return this.buildMessage
      if (this.isInstalling) return this.install.label || 'Setting up your server…'
      return ''
    },
    powerColor () {
      if (this.pendingPower) return 'warning'
      switch (this.server.power_state) {
        case 'online': return 'primary'
        default: return 'secondary'
      }
    },
    powerLabel () {
      return this.pendingPower ? PENDING_LABELS[this.pendingPower.action] : this.server.power_state
    },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    openRename () {
      this.rename = { open: true, value: this.server.custom_label || '', saving: false, error: '' }
    },
    async saveRename () {
      const r = this.rename
      r.saving = true
      r.error = ''
      try {
        const label = await kvm.setLabel(this.hostingid, r.value.trim())
        this.server = {
          ...this.server,
          custom_label: label,
          label: label || (this.server.hostname !== '—' ? this.server.hostname : this.server.label),
        }
        r.open = false
        this.reportSuccess(label ? 'Server renamed' : 'Server name cleared')
      } catch (e) {
        r.error = e.message
      } finally {
        r.saving = false
      }
    },
    // Header power menu → POST /vm/power, then follow the server until it
    // actually reaches the new state. The menu stays locked the whole time.
    async runPower (action) {
      if (this.pendingPower) return
      this.pendingPower = { action, fromUptime: this.server.uptime }
      try {
        await kvm.powerAction(this.server.id, action)
      } catch (e) {
        this.pendingPower = null
        this.reportError(e)
        return
      }
      const verb = { reboot: 'Reboot', stop: 'Power off', start: 'Power on' }[action]
      this.reportSuccess(`${verb} request sent.`)
      this.followPower()
    },
    // Quiet re-reads (no skeleton) until settled or the budget runs out. A reboot
    // never leaves 'online', so it counts as done once uptime drops below where
    // it was (the VM restarted); if uptime isn't reported, a few ticks will do.
    followPower () {
      let tries = 0
      const tick = async () => {
        this.pendingTimer = null
        const pending = this.pendingPower
        if (!pending) return
        tries++
        try {
          this.server = await kvm.getServer(this.hostingid)
        } catch (e) { /* transient — keep going until the budget runs out */ }
        const s = this.server
        let settled
        if (pending.action === 'start') settled = s.power_state === 'online'
        else if (pending.action === 'stop') settled = s.power_state === 'offline'
        else if (s.uptime != null && pending.fromUptime != null) settled = s.power_state === 'online' && s.uptime < pending.fromUptime
        else settled = tries >= 3
        if (settled || tries >= POWER_WATCH_TRIES) this.pendingPower = null
        else this.pendingTimer = setTimeout(tick, POWER_WATCH_INTERVAL)
      }
      this.pendingTimer = setTimeout(tick, POWER_WATCH_INTERVAL)
    },
    stopFollowPower () {
      if (this.pendingTimer) {
        clearTimeout(this.pendingTimer)
        this.pendingTimer = null
      }
      this.pendingPower = null
    },
    async loadServer () {
      try {
        this.loading = true
        this.error = null
        this.server = await kvm.getServer(this.hostingid)
        this.managePolling()
      } catch (e) {
        this.error = e
        this.stopPolling()
      } finally {
        this.loading = false
      }
    },
    // Poll while the entitlement is provisioning, the VM is coming up, the app is
    // still installing, or the Docker app chosen with the server is still going
    // on; stop once it settles (ready or failed).
    managePolling () {
      const dockerApp = this.server.docker_app_install
      const dockerAppPending = !!(dockerApp && ['pending', 'installing'].includes(dockerApp.state))
      if (this.isBuilding || this.isInstalling || dockerAppPending) {
        if (!this.pollTimer) this.pollTimer = setInterval(this.loadServer, POLL_INTERVAL)
      } else {
        this.stopPolling()
      }
    },
    stepIcon (status) {
      switch (status) {
        case 'done': return 'mdi-check-circle'
        case 'running': return 'mdi-progress-clock'
        case 'error': return 'mdi-alert-circle'
        case 'skipped': return 'mdi-minus-circle-outline'
        default: return 'mdi-circle-outline' // pending
      }
    },
    stepColor (status) {
      switch (status) {
        case 'done': return 'success'
        case 'running': return 'primary'
        case 'error': return 'error'
        default: return 'grey' // pending / skipped
      }
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    // After a power action, re-read the server until power_state reaches `expected`
    // ('online' / 'offline') or we give up. A graceful shutdown or boot takes a
    // while, so a single refresh right after the request still reads the old
    // state. Quiet on purpose: loadServer() flips `loading`, which swaps the
    // sub-page for a skeleton on every tick (and would unmount the caller).
    // Pages waiting on a powered-off server (Terminal / Console) pass a slower
    // interval and no try limit, and cancel via the returned function on leave.
    watchPower (expected, { interval = POWER_WATCH_INTERVAL, tries: maxTries = POWER_WATCH_TRIES } = {}) {
      this.stopPowerWatch()
      let tries = 0
      const tick = async () => {
        tries++
        try {
          this.server = await kvm.getServer(this.hostingid)
        } catch (e) { /* transient — keep trying until the budget runs out */ }
        const settled = !expected || this.server.power_state === expected
        this.powerTimer = settled || tries >= maxTries ? null : setTimeout(tick, interval)
      }
      this.powerTimer = setTimeout(tick, interval)
      return this.stopPowerWatch
    },
    stopPowerWatch () {
      if (this.powerTimer) {
        clearTimeout(this.powerTimer)
        this.powerTimer = null
      }
    },
    // Picker emitted 'provisioning' after a successful POST /provision — flip the
    // gate optimistically and start polling for the build to finish.
    onProvisioningStarted () {
      this.server = { ...this.server, provisioning_state: 'provisioning' }
      this.loadServer()
    },
  },
  watch: {
    // Tell the shared sidebar whether this server gets the Docker menu item
    // (every load path — initial, polls, power follow-ups — replaces `server`).
    'server.docker_enabled' (enabled) {
      if (this.server.id) setKvmFeature('docker', this.server.id, enabled)
    },
    hostingid () {
      this.stopPolling()
      this.stopPowerWatch()
      this.stopFollowPower()
      this.server = {}
      this.loadServer()
    },
  },
  created () {
    this.loadServer()
  },
  beforeDestroy () {
    this.stopPolling()
    this.stopPowerWatch()
    this.stopFollowPower()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Persistent header field labels (Hostname / Primary IP / Location / OS). Replaces
// the faint v-list-item-subtitle grey so labels meet AA in both themes.
.kvm-shell__label {
  @include kvm-secondary-text;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 2px;
}

// Calm helper/hint copy on the build/install state screens.
.kvm-shell__hint {
  @include kvm-secondary-text;
}

// De-emphasised (pending/skipped) install steps — muted but still AA-legible.
.kvm-shell__muted {
  @include kvm-secondary-text;
}

// Visually-hidden text that keeps meaning available to screen readers (chip
// context, per-step status that is otherwise only conveyed by icon + colour).
.kvm-shell__sr {
  @include kvm-sr-only;
}

// Single persistent, always-rendered polite live region (visually hidden). Its
// text is the statusAnnouncement computed, announcing transient state changes.
.kvm-sr-status {
  @include kvm-sr-only;
}
</style>
